// frontend/src/App.jsx
import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Loader } from 'lucide-react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/layout/DashboardLayout';
import ErrorBoundary from './components/ErrorBoundary';
import { initGA } from './config/analytics';
import { usePageTracking } from './hooks/useAnalytics';

// Lazy-loaded route components — each becomes its own chunk so a user
// landing on /login doesn't pay the cost of recharts/jszip/papaparse.
const LoginPage        = lazy(() => import('./pages/auth/LoginPage'));
const AuthVerify       = lazy(() => import('./pages/auth/AuthVerify'));
const AuthSuccess      = lazy(() => import('./pages/auth/AuthSuccess'));
const AuthError        = lazy(() => import('./pages/auth/AuthError'));
const DashboardHome    = lazy(() => import('./pages/dashboard/DashboardHome'));
const ExportCreator    = lazy(() => import('./pages/exports/ExportCreator'));
const ExportHistory    = lazy(() => import('./pages/exports/ExportHistory'));
const SuccessPage      = lazy(() => import('./pages/credits/Success'));
const CreditsPage      = lazy(() => import('./pages/credits/CreditsPage'));
const SettingsPage     = lazy(() => import('./pages/settings/SettingsPage'));
const UnsubscribePage  = lazy(() => import('./pages/settings/UnsubscribePage'));
const SupportPage      = lazy(() => import('./pages/support/SupportPage'));
const FAQPage          = lazy(() => import('./pages/support/FAQPage'));
const UserGuidePage    = lazy(() => import('./pages/support/UserGuidePage'));
const RefundPolicy     = lazy(() => import('./pages/support/RefundPolicy'));
const PrivacyPolicy    = lazy(() => import('./pages/support/PrivacyPolicy'));
const TermsOfService   = lazy(() => import('./pages/support/TermsOfService'));
const AdminDashboard   = lazy(() => import('./pages/dashboard/AdminDashboard'));

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <Loader className="w-6 h-6 text-blue-500 animate-spin" aria-label="Loading" />
  </div>
);

// Component to handle page tracking
const AppWithTracking = () => {
  usePageTracking();
  return null;
};

const App = () => {
  useEffect(() => {
    initGA();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppWithTracking />
          <ToastContainer position="top-right" autoClose={3000} />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/verify" element={<AuthVerify />} />
              <Route path="/auth/success" element={<AuthSuccess />} />
              <Route path="/auth/error" element={<AuthError />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />

              {/* Protected Routes */}
              <Route element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }>
                <Route path="/dashboard" element={<DashboardHome />} />
                <Route path="/dashboard/admin" element={<AdminDashboard />} />
                <Route path="/dashboard/exports" element={<ExportCreator />} />
                <Route path="/dashboard/exports/history" element={<ExportHistory />} />
                <Route path="/dashboard/credits/success" element={<SuccessPage />} />
                <Route path="/dashboard/credits" element={<CreditsPage />} />
                <Route path="/dashboard/settings" element={<SettingsPage />} />
                <Route path="/dashboard/support" element={<SupportPage />} />
                <Route path="/dashboard/help" element={<SupportPage />} />
                <Route path="/dashboard/faq" element={<FAQPage />} />
                <Route path="/dashboard/user-guide" element={<UserGuidePage />} />
                <Route path="/support/refund-policy" element={<RefundPolicy />} />
                <Route path="/support/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/support/terms-of-service" element={<TermsOfService />} />
              </Route>

              {/* Redirect root to dashboard or login */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* Add this route to handle the redirect */}
              <Route path="/credits/success" element={<Navigate to="/dashboard/credits/success" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
